import {
  CellState,
  CellType,
  ClueType,
  FeedFormat,
  ICell,
  IClue,
  IPuzzleData,
  PuzzleType,
} from 'js/crossword/types';
import { replaceAll } from 'framework/utils/misc-utils';
import { unpackData } from "js/crossword/converters/utils/unpackData"

interface ITCAMiniRoot {
  data: ITCAMiniData;
  metaData: ITCAMiniMeta;
}

interface ITCAMiniMeta {
  author: string;
  date: string;
  description: string;
  feedSlug: string;
  format: FeedFormat;
  id: string;
  title: string;
}

interface ITCAMiniData {
  alphabet: string;
  crossword: {
    clues: [
      {
        clue: ITCAMiniClue[];
        title: string;
      },
      {
        clue: ITCAMiniClue[];
        title: string;
      }
    ],
    grid: ITCAMiniGrid,
    word: ITCAMiniWord[];
  }
}

interface ITCAMiniGrid {
  width: number;
  height: number;
  cell: ITCAMiniCell[];
}

interface ITCAMiniClue {
  format: string;
  number: string;
  text: string;
  word: string;
}

interface ITCAMiniCell {
  x: number;
  y: number;
  type: ITCAMiniCellType;
  solution: string;
  number: number;
};

interface ITCAMiniWord {
  id: string;
  x: string;
  y: string;
};

enum ITCAMiniClueType {
  ACROSS = "across",
  DOWN = "down",
}

enum ITCAMiniCellType {
  BLOCK = "block",
}

export default function convertTCAMini(rawData: any): IPuzzleData {
  const unpackedData: ITCAMiniRoot = unpackData(rawData);
  const data: ITCAMiniData = unpackedData.data;
  const metaData: ITCAMiniMeta = unpackedData.metaData;

  const columns: number = Number(data.crossword.grid.width);
  const rows: number = Number(data.crossword.grid.height);
  const clueCount: number = Number(data.crossword.clues[0].clue.length); // get the amount of clues in one row

  const clues: IClue[] = [];

  data.crossword.clues.forEach((clueGroup: any, outerIndex: number) => {
    clues.push(...clueGroup.clue.map((clueData: ITCAMiniClue, innerIndex: number) => {
      const words: any = data.crossword.word;
      const clueType: ClueType = clueGroup.title.b.toLowerCase() === ITCAMiniClueType.ACROSS ? ClueType.Across : ClueType.Down;
      return {
        id: (outerIndex * clueCount + innerIndex).toString(),
        number: clueData.word,
        type: clueType,
        text: cleanUpClueText(clueData.text),
        cells: getClueCells(words.find((w: ITCAMiniWord) => w.id === clueData.word), clueType === ClueType.Across ? columns : rows), // cellIds
      };
    }));
  });

  const cells: ICell[] = data.crossword.grid.cell.map((cellRaw: any) => {
    const cell: ITCAMiniCell = cellRaw as ITCAMiniCell;
    const id: string = String(Number(cell.x) + Number(cell.y - 1) * columns);
    return {
      id: id,
      type: 'type' in cell && (cell.type.toLowerCase() === ITCAMiniCellType.BLOCK) ? CellType.Empty : CellType.Regular,
      letter: '',
      state: CellState.Normal,
      circle: false,
      number: 'number' in cell ? cell.number : null,
      x: Number(cell.x) - 1,
      y: Number(cell.y) - 1,
      solution: 'solution' in cell ? cellRaw.solution : null,
    };
  });

  cells.sort((a: ICell, b: ICell) => Number(a.id) - Number(b.id));

  return {
    type: PuzzleType.Regular,
    id: metaData.id,
    metaData: {
      title: metaData.title || 'Crossword',
      author: metaData.author,
      date: metaData.date,
      format: metaData.format,
    },
    columns,
    rows,
    clues,
    cells,
  };
}

function cleanUpClueText(text: string): string {
  return replaceAll(text, 'É', '');
}

function getClueCells(word: any, size: number): string[] {
  const horCoord = word.x.split("-").map(Number);
  const vertCoord = word.y.split("-").map(Number);

  if (horCoord.length === 2) {
    const row = vertCoord[0];
    var x: string[] = getCellNumber(horCoord[0], horCoord[1])
      .map(n => ((row - 1) * size) + n)
      .map(String);

    return x;
  }

  const column = horCoord[0];
  var y: string[] = getCellNumber(vertCoord[0], vertCoord[1])
    .map(n => ((n - 1) * size) + column)
    .map(String);

  return y;
}

function getCellNumber(first: number, last: number): number[] {
  return new Array((last + 1) - first).fill(0).map((_, i) => first + i);
}